// Stock → Reports (spec/13 §6.4 Reports; `inventory:reports`): four reports, each a table with its totals and a CSV —
//
//   Stock value     what the stock on hand is worth now, by item, at each item's exact average cost
//   Usage           over a range of business days: sold, wasted, staff meals, issued, received, counted, adjusted
//   Count variance  each count posted in the range and what it found, in units and rupees, and by item
//   Margins         each dish (and size) with a recipe: its price, its price before tax, what its recipe costs, the
//                   margin and margin %, marked when an ingredient has never been costed
//
// The figures are the server's; the screen only lays them out (`../reports/report-kit.tsx`, the Reports area's own
// cards), so the stock reports read like every other report. Cost of goods is not posted to the money ledger
// (ADR-0040 §8): these are management figures.

import type { StockMarginLine, StockMovementKind, StockUsageLine, StockValuationLine, StockVarianceCount, StockVarianceItem } from '@clavian/api-client';
import { REPORT_PRESETS, reportRangeOf, type ReportPreset } from '@clavian/domain/reports';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Badge, BarChart, ChartEmpty, SegmentedControl, Select, StatCard, Table, type TableColumn } from '@clavian/ui-web';
import type { ReactNode } from 'react';

import { useCurrentVenue } from '../../shell/scope';
import { Toolbar } from '../../ui';
import { ReportCard, ReportKpis, ReportNote, ReportPage } from '../reports/report-kit';
import { csvMoney, ExportCsv, percentOf, TabPending } from '../reports/tab-kit';
import { MINUS, NO_FIGURE, useStockFormat, useStockUnits } from './format';
import { useMarginsReport, useUsageReport, useValuationReport, useVarianceReport } from './queries';
import type { StockSearch } from './StockPage';

export const STOCK_REPORTS = ['value', 'usage', 'variance', 'margins'] as const;
export type StockReportName = (typeof STOCK_REPORTS)[number];
/** The usage report's columns, in the order a café reads them: out first, then in. */
const USAGE_KINDS: readonly StockMovementKind[] = ['sale', 'waste', 'staff_meal', 'issue', 'refund', 'receive', 'count', 'adjust'];

/** The report and its range are the URL's (`?report=&range=`), so a report is a link to share. */
export function StockReportsTab({ search, setSearch }: { readonly search: StockSearch; readonly setSearch: (patch: Partial<StockSearch>) => void }): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const report: StockReportName = search.report ?? 'value';
  const preset: ReportPreset = search.range ?? 'last7';
  const today = format.businessDate(new Date().toISOString());
  const range = reportRangeOf(preset, today);
  const ranged = report === 'usage' || report === 'variance';
  return (
    <div className="console-stock-tab">
      <Toolbar label={t('stock.reports.toolbar')} className="console-stock-toolbar">
        <SegmentedControl<StockReportName>
          label={t('stock.reports.which')}
          value={report}
          onValueChange={(value) => setSearch({ report: value === 'value' ? undefined : value })}
          options={STOCK_REPORTS.map((value) => ({ value, label: t(`stock.reports.tab.${value}`) }))}
        />
        {ranged ? (
          <div className="console-stock-toolbar__kind">
            <Select aria-label={t('stock.reports.range')} value={preset} onValueChange={(value) => setSearch({ range: value === 'last7' ? undefined : (value as ReportPreset) })} options={REPORT_PRESETS.map((value) => ({ value, label: t(`reports.range.${value}`) }))} />
          </div>
        ) : null}
      </Toolbar>
      {report === 'value' ? <ValueReport /> : null}
      {report === 'usage' ? <UsageReport from={range.from} to={range.to} /> : null}
      {report === 'variance' ? <VarianceReport from={range.from} to={range.to} /> : null}
      {report === 'margins' ? <MarginsReport /> : null}
    </div>
  );
}

/** A quantity as a CSV cell: base units, exact, as the wire has it ("3200"); the unit is its own column. */
const csvQuantity = (quantity: number) => String(quantity);

/** Basis points as a CSV percentage, exactly: 8230 → "82.30", −125 → "-1.25". */
function percentText(bps: number): string {
  const sign = bps < 0 ? '-' : '';
  const magnitude = Math.abs(bps);
  return `${sign}${Math.trunc(magnitude / 100)}.${String(magnitude % 100).padStart(2, '0')}`;
}

/** "−₹1,240.00" with a real minus sign, "+₹30.00" for a gain. */
function signedMoney(text: string, amount: number): string {
  if (amount > 0) return `+${text}`;
  if (amount < 0) return `${MINUS}${text.replace('-', '')}`;
  return text;
}

// -------------------------------------------------------------------------------------------------
// Stock value
// -------------------------------------------------------------------------------------------------

function ValueReport(): ReactNode {
  const units = useStockUnits();
  const { t } = useTranslation('console');
  const venue = useCurrentVenue();
  const format = useVenueFormatter();
  const stock = useStockFormat();
  const report = useValuationReport(venue.organizationId, venue.id);
  if (report.data === undefined) return <TabPending query={report} unavailable={t('stock.reports.unavailable')} forbidden={t('stock.reports.notPermitted')} />;
  const data = report.data;
  const items = [...data.items].sort((a, b) => b.value.amount - a.value.amount);
  const uncosted = data.items.filter((item) => item.averageUnitCost === null && item.onHand !== 0);
  const columns: TableColumn<StockValuationLine>[] = [
    { key: 'name', header: t('stock.column.item'), cell: (row) => row.name },
    { key: 'kind', header: t('stock.reports.kind'), cell: (row) => <span className="console-muted">{t(`stock.kind.${row.kind}`)}</span> },
    { key: 'onHand', header: t('stock.column.onHand'), numeric: true, cell: (row) => <span className="console-num">{stock.quantity(row.onHand, units(row.stockItemId, row.baseUnit))}</span> },
    { key: 'cost', header: t('stock.column.averageCost'), numeric: true, cell: (row) => <span className="console-num console-muted">{stock.unitCost(row.averageUnitCost, units(row.stockItemId, row.baseUnit))}</span> },
    { key: 'value', header: t('stock.column.value'), numeric: true, cell: (row) => <span className="console-money">{format.money(row.value)}</span> },
  ];
  return (
    <ReportPage>
      <ReportKpis columns={3} label={t('stock.reports.tab.value')}>
        <StatCard label={t('stock.reports.value.total')} value={<span className="console-num">{format.money(data.total, 'noFraction')}</span>} hint={t('stock.reports.value.asOf', { when: format.dateTime(data.asOf, { date: 'medium', time: 'short' }) })} />
        <StatCard label={t('stock.reports.value.items')} value={<span className="console-num">{format.number(data.items.length)}</span>} hint={t('stock.reports.value.itemsHint')} />
        <StatCard label={t('stock.reports.value.uncosted')} value={<span className="console-num">{format.number(uncosted.length)}</span>} hint={uncosted.length === 0 ? t('stock.reports.value.allCosted') : t('stock.reports.value.uncostedHint')} />
      </ReportKpis>
      <ReportCard title={t('stock.reports.value.top')} description={t('stock.reports.value.topHint')}>
        {items.every((item) => item.value.amount <= 0) ? (
          <ChartEmpty>{t('stock.reports.value.none')}</ChartEmpty>
        ) : (
          <BarChart
            label={t('stock.reports.value.top')}
            limit={6}
            rows={items.filter((item) => item.value.amount > 0).map((item) => ({ key: item.stockItemId, label: item.name, value: item.value.amount, display: format.money(item.value), meta: stock.quantity(item.onHand, units(item.stockItemId, item.baseUnit)) }))}
          />
        )}
      </ReportCard>
      <ReportCard
        title={t('stock.reports.value.byItem')}
        description={t('stock.reports.value.byItemHint')}
        flush
        action={
          <ExportCsv
            fileName={`stock-value-${data.asOf.slice(0, 10)}.csv`}
            header={['item', 'kind', 'on_hand', 'unit', 'average_cost_amount', 'average_cost_for_units', 'value', 'currency']}
            rows={data.items.map((row) => [row.name, row.kind, csvQuantity(row.onHand), row.baseUnit, row.averageUnitCost === null ? '' : csvMoney(row.averageUnitCost.amount), row.averageUnitCost?.quantity ?? '', csvMoney(row.value), data.currency])}
          />
        }
      >
        <Table caption={t('stock.reports.value.byItem')} captionHidden columns={columns} rows={items} rowKey={(row) => row.stockItemId} />
        <TotalLine label={t('stock.reports.total')} value={format.money(data.total)} />
      </ReportCard>
      {uncosted.length > 0 ? <ReportNote>{t('stock.reports.value.uncostedNote', { names: uncosted.map((item) => item.name).join(', ') })}</ReportNote> : null}
    </ReportPage>
  );
}

/** A table's total under it, as the report cards' strong row. */
function TotalLine({ label, value }: { readonly label: string; readonly value: string }): ReactNode {
  return (
    <p className="console-stock-totalline">
      <span>{label}</span>
      <span className="console-money">{value}</span>
    </p>
  );
}

// -------------------------------------------------------------------------------------------------
// Usage
// -------------------------------------------------------------------------------------------------

function UsageReport({ from, to }: { readonly from: string; readonly to: string }): ReactNode {
  const units = useStockUnits();
  const { t } = useTranslation('console');
  const venue = useCurrentVenue();
  const format = useVenueFormatter();
  const stock = useStockFormat();
  const report = useUsageReport(venue.organizationId, venue.id, from, to);
  if (report.data === undefined) return <TabPending query={report} unavailable={t('stock.reports.unavailable')} forbidden={t('stock.reports.notPermitted')} />;
  const data = report.data;
  const currency = data.currency;
  const total = (kind: StockMovementKind) => data.totals.find((one) => one.kind === kind)?.value.amount ?? 0;
  const used = (row: StockUsageLine) => row.byKind.filter((one) => one.kind === 'sale' || one.kind === 'waste' || one.kind === 'staff_meal' || one.kind === 'issue').reduce((sum, one) => sum + one.value.amount, 0);
  const rows = [...data.items].sort((a, b) => used(a) - used(b));
  const present = USAGE_KINDS.filter((kind) => data.items.some((row) => row.byKind.some((one) => one.kind === kind)));
  const cell = (row: StockUsageLine, kind: StockMovementKind) => {
    const entry = row.byKind.find((one) => one.kind === kind);
    return entry === undefined || entry.quantity === 0 ? <span className="console-subtle">{NO_FIGURE}</span> : <span className="console-num">{stock.change(entry.quantity, units(row.stockItemId, row.baseUnit))}</span>;
  };
  const columns: TableColumn<StockUsageLine>[] = [
    { key: 'name', header: t('stock.column.item'), cell: (row) => row.name },
    ...present.map((kind) => ({ key: kind, header: t(`stock.movement.${kind}`), numeric: true, cell: (row: StockUsageLine) => cell(row, kind) })),
    { key: 'used', header: t('stock.reports.usage.usedValue'), numeric: true, cell: (row) => <span className="console-money">{signedMoney(format.money({ amount: used(row), currency }), used(row))}</span> },
  ];
  const usedTotal = rows.reduce((sum, row) => sum + used(row), 0);
  return (
    <ReportPage>
      <ReportKpis label={t('stock.reports.tab.usage')}>
        <StatCard label={t('stock.reports.usage.sold')} value={<span className="console-num">{format.money({ amount: Math.abs(total('sale')), currency }, 'noFraction')}</span>} hint={t('stock.reports.usage.soldHint')} />
        <StatCard
          label={t('stock.reports.usage.wasted')}
          value={<span className="console-num">{format.money({ amount: Math.abs(total('waste') + total('staff_meal')), currency }, 'noFraction')}</span>}
          hint={t('stock.reports.usage.wastedHint', { meals: format.money({ amount: Math.abs(total('staff_meal')), currency }) })}
          tone={total('waste') < 0 ? 'warn' : 'default'}
        />
        <StatCard label={t('stock.reports.usage.received')} value={<span className="console-num">{format.money({ amount: total('receive'), currency }, 'noFraction')}</span>} hint={t('stock.reports.usage.receivedHint')} />
        <StatCard
          label={t('stock.reports.usage.corrections')}
          value={<span className="console-num">{signedMoney(format.money({ amount: total('count') + total('adjust'), currency }, 'noFraction'), total('count') + total('adjust'))}</span>}
          hint={t('stock.reports.usage.correctionsHint')}
        />
      </ReportKpis>
      <ReportCard
        title={t('stock.reports.usage.byItem')}
        description={t('stock.reports.usage.byItemHint', { from: format.custom(`${data.window.from}T12:00:00Z`, { day: 'numeric', month: 'short' }), to: format.custom(`${data.window.to}T12:00:00Z`, { day: 'numeric', month: 'short' }) })}
        flush
        action={
          <ExportCsv
            fileName={`stock-usage-${data.window.from}-${data.window.to}.csv`}
            header={['item', 'unit', 'kind', 'quantity', 'value', 'currency']}
            rows={data.items.flatMap((row) => row.byKind.map((entry) => [row.name, row.baseUnit, entry.kind, csvQuantity(entry.quantity), csvMoney(entry.value), currency]))}
          />
        }
      >
        {rows.length === 0 ? (
          <ChartEmpty>{t('stock.reports.usage.none')}</ChartEmpty>
        ) : (
          <>
            <Table caption={t('stock.reports.usage.byItem')} captionHidden columns={columns} rows={rows} rowKey={(row) => row.stockItemId} />
            <TotalLine label={t('stock.reports.usage.usedTotal')} value={signedMoney(format.money({ amount: usedTotal, currency }), usedTotal)} />
          </>
        )}
      </ReportCard>
    </ReportPage>
  );
}

// -------------------------------------------------------------------------------------------------
// Count variance
// -------------------------------------------------------------------------------------------------

function VarianceReport({ from, to }: { readonly from: string; readonly to: string }): ReactNode {
  const units = useStockUnits();
  const { t } = useTranslation('console');
  const venue = useCurrentVenue();
  const format = useVenueFormatter();
  const stock = useStockFormat();
  const report = useVarianceReport(venue.organizationId, venue.id, from, to);
  if (report.data === undefined) return <TabPending query={report} unavailable={t('stock.reports.unavailable')} forbidden={t('stock.reports.notPermitted')} />;
  const data = report.data;
  const items = [...data.items].sort((a, b) => a.varianceValue.amount - b.varianceValue.amount);
  const losses = items.filter((item) => item.varianceValue.amount < 0).reduce((sum, item) => sum + item.varianceValue.amount, 0);
  const countColumns: TableColumn<StockVarianceCount>[] = [
    { key: 'when', header: t('stock.reports.variance.posted'), cell: (row) => <span className="console-num">{format.dateTime(row.submittedAt, { date: 'medium', time: 'short' })}</span> },
    { key: 'kind', header: t('stock.reports.kind'), cell: (row) => t(`stock.counts.kind.${row.kind}`) },
    { key: 'by', header: t('stock.deliveries.by'), cell: (row) => <span className="console-muted">{row.submittedByName ?? t('stock.history.system')}</span> },
    { key: 'lines', header: t('stock.reports.variance.items'), numeric: true, cell: (row) => <span className="console-num">{format.number(row.lineCount)}</span> },
    { key: 'value', header: t('stock.count.differenceValue'), numeric: true, cell: (row) => <span className={row.varianceValue.amount < 0 ? 'console-money console-stock-change--out' : 'console-money'}>{signedMoney(format.money(row.varianceValue), row.varianceValue.amount)}</span> },
  ];
  const itemColumns: TableColumn<StockVarianceItem>[] = [
    { key: 'name', header: t('stock.column.item'), cell: (row) => row.name },
    { key: 'quantity', header: t('stock.count.difference'), numeric: true, cell: (row) => <span className="console-num">{stock.change(row.varianceQuantity, units(row.stockItemId, row.baseUnit))}</span> },
    { key: 'value', header: t('stock.count.differenceValue'), numeric: true, cell: (row) => <span className={row.varianceValue.amount < 0 ? 'console-money console-stock-change--out' : 'console-money'}>{signedMoney(format.money(row.varianceValue), row.varianceValue.amount)}</span> },
  ];
  return (
    <ReportPage>
      <ReportKpis columns={3} label={t('stock.reports.tab.variance')}>
        <StatCard
          label={t('stock.reports.variance.net')}
          value={<span className="console-num">{signedMoney(format.money(data.total, 'noFraction'), data.total.amount)}</span>}
          hint={t('stock.reports.variance.netHint')}
          tone={data.total.amount < 0 ? 'warn' : 'default'}
        />
        <StatCard label={t('stock.reports.variance.losses')} value={<span className="console-num">{format.money({ amount: Math.abs(losses), currency: data.currency }, 'noFraction')}</span>} hint={t('stock.reports.variance.lossesHint')} />
        <StatCard label={t('stock.reports.variance.counts')} value={<span className="console-num">{format.number(data.counts.length)}</span>} hint={t('stock.reports.variance.countsHint')} />
      </ReportKpis>
      <ReportCard title={t('stock.reports.variance.byCount')} description={t('stock.reports.variance.byCountHint')} flush>
        {data.counts.length === 0 ? <ChartEmpty>{t('stock.reports.variance.none')}</ChartEmpty> : <Table caption={t('stock.reports.variance.byCount')} captionHidden columns={countColumns} rows={data.counts} rowKey={(row) => row.countId} />}
      </ReportCard>
      <ReportCard
        title={t('stock.reports.variance.byItem')}
        description={t('stock.reports.variance.byItemHint')}
        flush
        action={
          <ExportCsv
            fileName={`stock-variance-${data.window.from}-${data.window.to}.csv`}
            header={['item', 'unit', 'difference', 'value', 'currency']}
            rows={data.items.map((row) => [row.name, row.baseUnit, csvQuantity(row.varianceQuantity), csvMoney(row.varianceValue), data.currency])}
          />
        }
      >
        {items.length === 0 ? (
          <ChartEmpty>{t('stock.reports.variance.noItems')}</ChartEmpty>
        ) : (
          <>
            <Table caption={t('stock.reports.variance.byItem')} captionHidden columns={itemColumns} rows={items} rowKey={(row) => row.stockItemId} />
            <TotalLine label={t('stock.reports.total')} value={signedMoney(format.money(data.total), data.total.amount)} />
          </>
        )}
      </ReportCard>
    </ReportPage>
  );
}

// -------------------------------------------------------------------------------------------------
// Margins
// -------------------------------------------------------------------------------------------------

function MarginsReport(): ReactNode {
  const { t } = useTranslation('console');
  const venue = useCurrentVenue();
  const format = useVenueFormatter();
  const report = useMarginsReport(venue.organizationId, venue.id);
  if (report.data === undefined) return <TabPending query={report} unavailable={t('stock.reports.unavailable')} forbidden={t('stock.reports.notPermitted')} />;
  const data = report.data;
  const rows = [...data.products].sort((a, b) => (a.marginBps ?? 0) - (b.marginBps ?? 0));
  const incomplete = rows.filter((row) => !row.costComplete).length;
  const exclTax = rows.reduce((sum, row) => sum + row.priceExclTax.amount, 0);
  const margin = rows.reduce((sum, row) => sum + row.margin.amount, 0);
  const columns: TableColumn<StockMarginLine>[] = [
    {
      key: 'name',
      header: t('stock.reports.margins.product'),
      cell: (row) => (
        <span className="console-stock-margin-name">
          {row.name}
          {row.costComplete ? null : <Badge tone="warn">{t('stock.reports.margins.incomplete')}</Badge>}
        </span>
      ),
    },
    { key: 'price', header: t('stock.reports.margins.price'), numeric: true, cell: (row) => <span className="console-money console-muted">{format.money(row.price)}</span> },
    { key: 'excl', header: t('stock.reports.margins.priceExclTax'), numeric: true, cell: (row) => <span className="console-money">{format.money(row.priceExclTax)}</span> },
    { key: 'cost', header: t('stock.reports.margins.cost'), numeric: true, cell: (row) => <span className="console-money">{format.money(row.cost)}</span> },
    { key: 'margin', header: t('stock.reports.margins.margin'), numeric: true, cell: (row) => <span className="console-money">{format.money(row.margin)}</span> },
    { key: 'percent', header: t('stock.reports.margins.marginPercent'), numeric: true, cell: (row) => <span className="console-num">{percentOf(format, row.marginBps, 1)}</span> },
  ];
  return (
    <ReportPage>
      <ReportKpis columns={3} label={t('stock.reports.tab.margins')}>
        <StatCard
          label={t('stock.reports.margins.average')}
          value={<span className="console-num">{exclTax === 0 ? NO_FIGURE : format.number(margin / exclTax, { style: 'percent', maximumFractionDigits: 1 })}</span>}
          hint={t('stock.reports.margins.averageHint')}
        />
        <StatCard label={t('stock.reports.margins.costed')} value={<span className="console-num">{format.number(rows.length)}</span>} hint={t('stock.reports.margins.costedHint')} />
        <StatCard label={t('stock.reports.margins.incompleteCount')} value={<span className="console-num">{format.number(incomplete)}</span>} hint={t('stock.reports.margins.incompleteHint')} tone={incomplete > 0 ? 'warn' : 'default'} />
      </ReportKpis>
      <ReportCard title={t('stock.reports.margins.chart')} description={t('stock.reports.margins.chartHint')}>
        {rows.length === 0 ? (
          <ChartEmpty>{t('stock.reports.margins.none')}</ChartEmpty>
        ) : (
          <BarChart
            label={t('stock.reports.margins.chart')}
            max={10_000}
            limit={8}
            rows={[...rows].reverse().map((row) => ({
              key: `${row.productId}-${row.variantId ?? 'base'}`,
              label: row.name,
              value: Math.max(0, row.marginBps ?? 0),
              display: percentOf(format, row.marginBps, 1),
              meta: t('stock.reports.margins.meta', { cost: format.money(row.cost), price: format.money(row.priceExclTax) }),
            }))}
          />
        )}
      </ReportCard>
      <ReportCard
        title={t('stock.reports.margins.table')}
        description={t('stock.reports.margins.tableHint')}
        flush
        action={
          <ExportCsv
            fileName={`stock-margins-${data.asOf.slice(0, 10)}.csv`}
            header={['product', 'price', 'price_excl_tax', 'recipe_cost', 'margin', 'margin_percent', 'cost_complete', 'currency']}
            rows={rows.map((row) => [row.name, csvMoney(row.price), csvMoney(row.priceExclTax), csvMoney(row.cost), csvMoney(row.margin), row.marginBps === null ? '' : percentText(row.marginBps), row.costComplete ? 'yes' : 'no', data.currency])}
          />
        }
      >
        {rows.length === 0 ? <ChartEmpty>{t('stock.reports.margins.none')}</ChartEmpty> : <Table caption={t('stock.reports.margins.table')} captionHidden columns={columns} rows={rows} rowKey={(row) => `${row.productId}-${row.variantId ?? 'base'}`} />}
      </ReportCard>
      {incomplete > 0 ? <ReportNote>{t('stock.reports.margins.incompleteNote')}</ReportNote> : null}
    </ReportPage>
  );
}
